import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MultiSelect from "@/components/MultiSelect";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SPORTS, DEFAULT_SPORT, circuitsForSport, agesForSport, GRADES } from "@/lib/sports";

const STATES = ["FL", "TX", "GA", "CA", "OH", "NY", "PA", "IL", "CO", "AZ", "WA", "MA", "DC", "NC", "NJ", "MN", "MI", "MO", "VA", "WI", "IN", "TN"];
const GENDERS = [["M", "Boys"], ["F", "Girls"]];
const DOMAINS = [["team", "Teams"], ["event", "Events"]];

/**
 * AlertEditModal — edit an existing saved-search alert's filters (same options as
 * the main search page) while keeping the alert's name. PUTs to /saved-searches/{id}.
 */
export default function AlertEditModal({ alert, onClose, onSaved, token = "" }) {
    const [label, setLabel] = useState(alert.label || "");
    const [sport, setSport] = useState(alert.sports?.[0] || DEFAULT_SPORT);
    const [domains, setDomains] = useState(alert.domains || []);
    const [states, setStates] = useState(alert.states || []);
    const [ages, setAges] = useState(alert.ages || []);
    const [grades, setGrades] = useState(alert.grades || []);
    const [genders, setGenders] = useState(alert.genders || []);
    const [circuits, setCircuits] = useState(alert.circuits || []);
    const [nearZip, setNearZip] = useState(alert.near_zip || "");
    const [radius, setRadius] = useState(alert.radius_miles || "");
    const [search, setSearch] = useState(alert.search || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!label.trim()) { toast.error("Alert name is required"); return; }
        setSaving(true);
        try {
            const { data } = await api.put(`/saved-searches/${alert.id}${token ? `?token=${encodeURIComponent(token)}` : ""}`, {
                label: label.trim(),
                sports: [sport],
                domains,
                states,
                ages,
                grades,
                genders,
                circuits,
                near_zip: nearZip,
                radius_miles: radius ? String(radius) : "",
                search,
            });
            toast.success("Alert updated");
            onSaved(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update alert");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="alert-edit-modal" onMouseDown={onClose}>
            <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] text-white max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] sticky top-0 bg-[#141414] z-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#ff4500] font-semibold">Edit alert filters</div>
                    <button type="button" data-testid="alert-edit-close" onClick={onClose} className="text-[#a0a0a0] hover:text-white"><X className="h-4 w-4" /></button>
                </div>

                <div className="p-4 space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Alert name</label>
                        <Input
                            data-testid="alert-edit-name"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Sport</label>
                        <Select value={sport} onValueChange={setSport}>
                            <SelectTrigger data-testid="alert-edit-sport" className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-2 border-[#ff4500] text-xs uppercase tracking-wider">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none max-h-80">
                                {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Listings</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-domain" label="Listings" value={domains} onChange={setDomains} options={DOMAINS} /></div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">States</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-state" label="States" value={states} onChange={setStates} options={STATES.map((s) => [s, s])} /></div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Ages</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-age" label="Ages" value={ages} onChange={setAges} options={agesForSport(sport).map((a) => [a, a])} /></div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Grades</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-grade" label="Grades" value={grades} onChange={setGrades} options={GRADES.map((g) => [g, g])} /></div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Genders</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-gender" label="Genders" value={genders} onChange={setGenders} options={GENDERS} /></div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Circuits</label>
                            <div className="mt-1.5"><MultiSelect testid="alert-edit-circuit" label="Circuits" value={circuits} onChange={setCircuits} options={[...new Set([...circuitsForSport(sport), "Other", ...circuits])].map((c) => [c, c])} /></div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Near ZIP</label>
                            <Input
                                data-testid="alert-edit-zip"
                                value={nearZip}
                                onChange={(e) => setNearZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                                inputMode="numeric"
                                placeholder="30301"
                                className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Radius (mi)</label>
                            <Input
                                data-testid="alert-edit-radius"
                                type="number"
                                min={0}
                                value={radius}
                                onChange={(e) => setRadius(e.target.value)}
                                placeholder="Any"
                                disabled={!nearZip}
                                className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a] disabled:opacity-50"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Keyword</label>
                        <Input
                            data-testid="alert-edit-keyword"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="e.g. elite"
                            className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 px-4 py-3 border-t border-[#2a2a2a] sticky bottom-0 bg-[#141414]">
                    <div className="flex-1" />
                    <button type="button" onClick={onClose} className="text-xs uppercase tracking-wider px-4 py-2.5 border border-[#2a2a2a] hover:border-white">Cancel</button>
                    <button type="button" data-testid="alert-edit-save" onClick={save} disabled={saving} className="text-xs uppercase tracking-wider font-semibold px-4 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white disabled:opacity-50">
                        {saving ? "Saving…" : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}
